import { ApiQuestion, ApiAnswer } from "@/types";

/**
 * Helper function to extract step number from question_key
 * @param questionKey String in format "question_1", "question_2", etc.
 * @returns Step number or null if invalid format
 */
function extractStepFromQuestionKey(questionKey: string): number | null {
  const match = questionKey.match(/^question_(\d+)$/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Determines which step to resume the questionnaire from based on user's answers
 * @param answers Array of answers from the API
 * @returns The step number to resume from (1-8), or 1 if no progress found
 */
export function determineResumeStepFromAnswers(answers: ApiAnswer[]): number {
  if (!answers || answers.length === 0) {
    return 1; // Start from beginning if no answers found
  }

  // Find all answered steps
  const answeredSteps = new Set<number>();

  for (const answer of answers) {
    const stepNumber = extractStepFromQuestionKey(answer.question_key);
    if (stepNumber && answer.answer_text && answer.answer_text.trim() !== "") {
      answeredSteps.add(stepNumber);
    }
  }

  // Find the first unanswered step
  const totalSteps = 8;
  for (let step = 1; step <= totalSteps; step++) {
    if (!answeredSteps.has(step)) {
      return step;
    }
  }

  // If all steps are answered, user should be on completion page
  return totalSteps;
}

/**
 * Checks if the questionnaire is complete based on the answers data
 * @param answers Array of answers from the API
 * @returns true if all 8 steps have been answered
 */
export function isQuestionnaireCompleteFromAnswers(
  answers: ApiAnswer[]
): boolean {
  if (!answers || answers.length === 0) {
    return false;
  }

  // Check if we have answers for all 8 steps
  const answeredSteps = new Set<number>();

  for (const answer of answers) {
    const stepNumber = extractStepFromQuestionKey(answer.question_key);
    if (stepNumber && answer.answer_text && answer.answer_text.trim() !== "") {
      answeredSteps.add(stepNumber);
    }
  }

  // Check if all steps 1-8 are answered
  for (let step = 1; step <= 8; step++) {
    if (!answeredSteps.has(step)) {
      return false;
    }
  }

  return true;
}

/**
 * Gets the progress percentage of the questionnaire based on answers
 * @param answers Array of answers from the API
 * @returns Progress percentage (0-100)
 */
export function getQuestionnaireProgressFromAnswers(
  answers: ApiAnswer[]
): number {
  if (!answers || answers.length === 0) {
    return 0;
  }

  const answeredSteps = new Set<number>();

  for (const answer of answers) {
    const stepNumber = extractStepFromQuestionKey(answer.question_key);
    if (stepNumber && answer.answer_text && answer.answer_text.trim() !== "") {
      answeredSteps.add(stepNumber);
    }
  }

  const totalSteps = 8;
  const completedSteps = answeredSteps.size;

  return Math.round((completedSteps / totalSteps) * 100);
}

// Legacy functions for backward compatibility (deprecated)
/**
 * @deprecated Use determineResumeStepFromAnswers instead
 */
export function determineResumeStep(questions: ApiQuestion[]): number {
  if (!questions || questions.length === 0) {
    return 1;
  }

  const sortedQuestions = [...questions].sort(
    (a, b) => a.step_number - b.step_number
  );

  for (const question of sortedQuestions) {
    if (!question.question_text || question.question_text.trim() === "") {
      return question.step_number;
    }
  }

  const maxStep = Math.max(...sortedQuestions.map((q) => q.step_number));
  const totalSteps = 8;

  if (maxStep >= totalSteps) {
    return totalSteps;
  }

  return Math.min(maxStep + 1, totalSteps);
}

/**
 * @deprecated Use isQuestionnaireCompleteFromAnswers instead
 */
export function isQuestionnaireComplete(questions: ApiQuestion[]): boolean {
  if (!questions || questions.length === 0) {
    return false;
  }

  const answeredSteps = new Set<number>();

  for (const question of questions) {
    if (question.question_text && question.question_text.trim() !== "") {
      answeredSteps.add(question.step_number);
    }
  }

  for (let step = 1; step <= 8; step++) {
    if (!answeredSteps.has(step)) {
      return false;
    }
  }

  return true;
}

/**
 * @deprecated Use getQuestionnaireProgressFromAnswers instead
 */
export function getQuestionnaireProgress(questions: ApiQuestion[]): number {
  if (!questions || questions.length === 0) {
    return 0;
  }

  const answeredSteps = new Set<number>();

  for (const question of questions) {
    if (question.question_text && question.question_text.trim() !== "") {
      answeredSteps.add(question.step_number);
    }
  }

  const totalSteps = 8;
  const completedSteps = answeredSteps.size;

  return Math.round((completedSteps / totalSteps) * 100);
}
